/**
 * AnimatedSparkline Component
 * 
 * Lightweight sparkline with staggered bar animation.
 * Per design.md: 150ms per bar, CSS transforms only.
 */

import { motion } from 'framer-motion';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { cn } from '@uprosal/ui';

interface AnimatedSparklineProps {
    data: number[];
    className?: string;
    barClassName?: string;
    delay?: number;
}

export function AnimatedSparkline({
    data,
    className,
    barClassName,
    delay = 0,
}: AnimatedSparklineProps) {
    const prefersReducedMotion = useReducedMotion();
    const maxValue = Math.max(...data);

    // If user prefers reduced motion, render static bars
    if (prefersReducedMotion) {
        return (
            <div className={cn('flex items-end gap-0.5', className)}>
                {data.map((value, i) => (
                    <div
                        key={i}
                        className={cn('flex-1 bg-primary/20 rounded-t', barClassName)}
                        style={{ height: `${(value / maxValue) * 100}%` }}
                    />
                ))}
            </div>
        );
    }

    return (
        <div className={cn('flex items-end gap-0.5', className)}>
            {data.map((value, i) => (
                <motion.div
                    key={i}
                    className={cn('flex-1 bg-primary/20 rounded-t', barClassName)}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                        height: `${(value / maxValue) * 100}%`,
                        opacity: 1,
                    }}
                    transition={{
                        delay: delay + i * 0.05, // 50ms stagger
                        duration: 0.3,
                        ease: 'easeOut',
                    }}
                />
            ))}
        </div>
    );
}
